import { Box, BoxProps, Flex, HStack, Image, Text } from '@chakra-ui/react';
import ContentStatusBadge, { TContentStatusBadgeValue } from '@/modules/content/common/ContentStatusBadge';
import DomainIconTiles from '@/modules/content/common/DomainIconTiles';
import { getCreatorName } from '@/modules/content/common/creatorDisplay';
import useFitText from '@/hooks/useFitText';
import { TContent, TContentDomain } from '@/types/Content.types';

const DEFAULT_CARD_SIZE = '400px';

// Font bounds for the auto-fit body (text-forward cards).
const BODY_MIN_FONT_SIZE = 13;
const BODY_MAX_FONT_SIZE = 24;

// The clip fade for an auto-fit body too long even at the minimum size.
const CLIP_FADE = 'linear-gradient(to bottom, black 90%, transparent 100%)';

// The single backdrop for cards with no thumbnail — Figma "Dawn I": #4E1AEF → #DC733E.
const CARD_GRADIENT = 'linear-gradient(160deg, #4E1AEF 0%, #DC733E 100%)';

// Brand-purple (#4E1AEF, the Dawn I stop) transparency over a thumbnail. It covers only
// the top ~60% — strong behind the creator/title, fully transparent by 60% — so the lower
// part of the image shows through clean.
const IMAGE_OVERLAY =
  'linear-gradient(180deg, rgba(78, 26, 239, 0.9) 0%, rgba(78, 26, 239, 0.55) 30%, rgba(78, 26, 239, 0) 60%)';

// Every feed card shares creator + domains on top, followed by the title and body,
// over a thumbnail with a readability scrim or a gradient when there is none.
type Props = {
  content: TContent;
  size?: BoxProps['width'];
  compact?: boolean;
  shouldRaiseOnHover?: boolean;
  // Creator-facing surfaces pass this to overlay a status chip. Left unset on the public
  // feeds, where every item is published and a chip would be noise.
  status?: TContentStatusBadgeValue | null;
};

const ContentCard = ({
  content,
  size = DEFAULT_CARD_SIZE,
  compact = false,
  shouldRaiseOnHover = false,
  status = null,
}: Props) => {
  const image = content.effectiveThumbnailUrl ?? content.thumbnailUrl;

  // Text-forward cards — no thumbnail, or any post — grow the body to fill the empty card
  // space (and shrink a long one to fit), instead of a fixed size with dead space below.
  const fitBody = !image || content.type === 'post';
  const bodyFit = useFitText(content.description, { minFontSize: BODY_MIN_FONT_SIZE, maxFontSize: BODY_MAX_FONT_SIZE });

  return (
    <Flex
      direction="column"
      position="relative"
      width={size}
      aspectRatio={1}
      padding={compact ? '12px' : '16px'}
      borderRadius="12px"
      overflow="hidden"
      _hover={
        shouldRaiseOnHover
          ? {
              transform: 'translateY(-6px)',
              boxShadow: '0px 16px 32px 0px rgba(14, 0, 98, 0.22)',
            }
          : {}
      }
      transition={'transform 0.25s ease, box-shadow 0.25s ease'}
      background={image ? undefined : CARD_GRADIENT}
    >
      {/* Backdrop (behind the padded content). */}
      {image && (
        <>
          <Image
            src={image}
            alt=""
            loading="lazy"
            decoding="async"
            position="absolute"
            inset={0}
            width="100%"
            height="100%"
            objectFit="cover"
            transition="transform 0.45s ease"
            _groupHover={{ transform: 'scale(1.06)' }}
          />
          <Box position="absolute" inset={0} background={IMAGE_OVERLAY} />
        </>
      )}

      {/* Shared overlay. */}
      <Flex direction="column" position="relative" height="100%" width="100%">
        <HStack justify="space-between" align="center" gap={compact ? '6px' : '10px'}>
          <HStack gap={compact ? '7px' : '10px'} minWidth={0}>
            <Box
              width={compact ? '24px' : '30px'}
              height={compact ? '24px' : '30px'}
              borderRadius="full"
              overflow="hidden"
              flexShrink={0}
              backgroundColor="whiteAlpha.300"
              borderWidth={'2px'}
              borderColor={'white'}
              dropShadow={'rgba(14, 0, 98, 0.14)'}
            >
              <Image
                src={content.creator?.photoUrl || '/svg/generic-user.svg'}
                alt=""
                width="100%"
                height="100%"
                objectFit="cover"
              />
            </Box>
            <Text fontSize={compact ? '12px' : '14px'} fontWeight={600} color="rgba(255, 255, 255, 0.8)" noOfLines={1}>
              {getCreatorName(content.creator, content.isPlatformContent)}
            </Text>
          </HStack>
          {content.domains?.length > 0 && (
            <Box flexShrink={0}>
              <DomainIconTiles domains={content.domains as TContentDomain[]} max={3} />
            </Box>
          )}
        </HStack>

        <Text
          marginTop={compact ? '4px' : '6px'}
          fontFamily="Lora"
          fontStyle="italic"
          fontWeight={600}
          fontSize={compact ? '16px' : '22px'}
          color="white"
          lineHeight="1.25"
          noOfLines={2}
        >
          {content.title || 'Untitled'}
        </Text>

        {content.description &&
          (fitBody ? (
            // Uses the available body space: a short post reads large,
            // a long one steps down until it fits, and only an over-long one is clipped
            // with a fade rather than cut mid-line.
            <Box
              ref={bodyFit.containerRef}
              minHeight={0}
              marginTop={compact ? '4px' : '6px'}
              overflow="hidden"
              sx={bodyFit.isOverflowing ? { maskImage: CLIP_FADE, WebkitMaskImage: CLIP_FADE } : undefined}
            >
              <Text
                ref={bodyFit.contentRef}
                fontWeight={'bold'}
                color="white"
                lineHeight="1.2"
                fontSize={compact ? (content.type === 'post' ? '15px' : '12px') : `${bodyFit.fontSize}px`}
                fontFamily={'Urbanist'}
              >
                {content.description}
              </Text>
            </Box>
          ) : (
            <Text
              marginTop={compact ? '4px' : '6px'}
              fontSize={compact ? '12px' : '14px'}
              fontWeight={compact ? 700 : 600}
              color="white"
              lineHeight={compact ? '15px' : '20px'}
              noOfLines={6}
            >
              {content.description}
            </Text>
          ))}
      </Flex>

      {/* Keep the status badge aligned with the bottom-right content edge. */}
      {status && (
        <Box position="absolute" bottom={compact ? '12px' : '16px'} right={compact ? '12px' : '16px'}>
          <ContentStatusBadge status={status} />
        </Box>
      )}
    </Flex>
  );
};

export default ContentCard;
